import { PERMISSIONS } from "@shared/constants/permissions";
import AssetsManagement from "@shared/svg/assets-tile-icon.svg?react";
import PartIcon from "@shared/svg/part.svg?react";
import ProcedureIcon from "@shared/svg/procedures.svg?react";
import Button, { BUTTON_SIZES, BUTTON_VARIANTS } from "@shared/ui/Button";
import Label, { LABEL_SIZES } from "@shared/ui/Label";
import moment from "moment-timezone";
import {
  CalendarBlank,
  FolderOpen,
  PencilSimple,
  Repeat,
  Trash,
  CheckCircle,
} from "phosphor-react";
import React, { useMemo, useRef } from "react";
import { useIntl } from "react-intl";
import { useLocation } from "react-router-dom";
import useOnClickOutside from "use-onclickoutside";

import { HorizontalBarsIcon, HourglassIconSmall } from "~/icons/icons";

import usePermissions from "~/hooks/usePermissions";

const PreviewEvent = ({
  title,
  repeatIn,
  repeatInNumber,
  eventDate,
  description,
  ticketCreationIn,
  ticketCreationNumber,
  nextCreationDate,
  onOutsideClick = () => {},
  onEditButtonClick,
  onDeleteButtonClick,
  createdBy,
  procedureCount,
  inventoryPartsCount,
  tileVariant = false,
  machineName = "",
  dateFormat,
  endsOn,
  lastCompletedAt,
  allOccurrencesDone,
}: {
  title: string;
  repeatIn: string;
  repeatInNumber: number;
  eventDate: string;
  description: string;
  ticketCreationIn: string;
  ticketCreationNumber: number;
  nextCreationDate: string;
  onOutsideClick?: () => void;
  onEditButtonClick: () => void;
  onDeleteButtonClick: () => void;
  createdBy?: { _id: string };
  procedureCount: number;
  inventoryPartsCount: number;
  tileVariant?: boolean;
  machineName?: string;
  dateFormat: string;
  endsOn?: string;
  lastCompletedAt?: string;
  allOccurrencesDone?: boolean;
}) => {
  const ref = useRef(null);
  const {
    messages: { preventiveMaintenance: PM },
  } = useIntl();
  const { canChange, canDelete } = usePermissions();

  const { pathname } = useLocation();

  const isSharedAssetsPage = useMemo(
    () => pathname.includes("/app/asset-hub/shared"),
    [pathname],
  );

  const momentEventDate = moment(eventDate);

  const canEdit =
    canChange(PERMISSIONS.assets.update, createdBy) && !isSharedAssetsPage;
  const allowDeletion =
    canDelete(PERMISSIONS.assets.delete, createdBy) && !isSharedAssetsPage;

  useOnClickOutside(ref, onOutsideClick);

  return (
    <section className="preview-event" ref={ref}>
      <div className="preview-event-header">
        <span className="flex shrink-0 items-center justify-center w-2xl h-2xl mr-[11px] bg-mint-light rounded-full">
          {/* @ts-ignore */}
          <HourglassIconSmall />
        </span>
        <Label size={LABEL_SIZES.LARGE} className="line-clamp-2 flex-1">
          {title}
        </Label>
      </div>

      <div className="preview-event-body">
        {tileVariant && machineName && (
          <div className="meta-data">
            <p className="repeat">
              <span className="peb-icon h-lg w-lg">
                <AssetsManagement size={16} />
              </span>
              <span className="flex whitespace-nowrap">
                {PM?.assetTitle}&nbsp; • &nbsp;
              </span>
              <span className="frequency-time text-black">{machineName}</span>
            </p>
          </div>
        )}

        {!repeatInNumber && momentEventDate.isSameOrBefore(moment()) ? (
          <div className="meta-data">
            <p className="date-time-progress">
              <span className="peb-icon">
                <CalendarBlank size={16} />
              </span>
              {PM?.lastCompleted}&nbsp; • &nbsp;
              <span className="date text-black">
                {moment(eventDate).format(dateFormat)}
              </span>
            </p>
          </div>
        ) : allOccurrencesDone ? (
          <>
            <div className="meta-data">
              <p className="date-time-progress">
                <span className="peb-icon">
                  <CalendarBlank size={16} />
                </span>
                {PM?.lastCompleted}&nbsp; • &nbsp;
                <span className="date text-black">
                  {moment(lastCompletedAt).format(dateFormat)}
                </span>
              </p>
            </div>

            {endsOn && (
              <div className="meta-data">
                <p className="date-time-progress">
                  <span className="peb-icon">
                    <CheckCircle size={16} />
                  </span>
                  {PM?.allOccurrencesDone}
                </p>
              </div>
            )}
          </>
        ) : (
          <>
            <div className="meta-data">
              <p className="date-time-progress">
                <span className="peb-icon">
                  <CalendarBlank size={16} />
                </span>
                {PM?.maintenanceOn}&nbsp; • &nbsp;
                <span className="date text-black">
                  {moment(eventDate).format(dateFormat)}
                </span>
              </p>
            </div>

            {endsOn && (
              <div className="meta-data">
                <p className="date-time-progress">
                  <span className="peb-icon">
                    <CalendarBlank size={16} />
                  </span>
                  {PM?.endsOn}&nbsp; • &nbsp;
                  <span className="date text-black">
                    {moment(endsOn).format(dateFormat)}
                  </span>
                </p>
              </div>
            )}
          </>
        )}

        <div className="meta-data">
          <p className="repeat">
            <span className="peb-icon">
              <Repeat size={16} />
            </span>
            {PM?.repeatEvery}&nbsp; • &nbsp;
            <span className="frequency-time text-black">
              {repeatInNumber !== 0 ? (
                <>
                  {repeatInNumber} {PM?.frequency[repeatIn.toLowerCase()]}
                </>
              ) : (
                <>{PM?.form.frequencyNoRepeat}</>
              )}
            </span>
          </p>
        </div>

        {ticketCreationIn && (
          <div className="meta-data">
            <p className="repeat">
              <span className="peb-icon">
                <FolderOpen size={16} />
              </span>
              {PM?.previewEvent.ticketCreationText} &nbsp; • &nbsp;
              <span className="event-creation-time text-black">
                {ticketCreationNumber +
                  " " +
                  PM?.frequency[ticketCreationIn.toLowerCase()] +
                  " "}
                {PM?.previewEvent.ticketCreationEnd}
              </span>
            </p>

            {!allOccurrencesDone && (
              <p className="sub-meta">
                {new Date().getTime() > new Date(nextCreationDate)?.getTime?.()
                  ? PM?.previewEvent.ticketBeforeCreationDate
                  : PM?.previewEvent.ticketCreationDate}{" "}
                {moment(nextCreationDate).format(dateFormat)}
              </p>
            )}
          </div>
        )}

        {!!description && (
          <div className="meta-data">
            <p className="pe-description !text-black">
              <span className="peb-icon">
                <HorizontalBarsIcon />
              </span>
              {description}
            </p>
          </div>
        )}

        {procedureCount > 0 && (
          <div className="meta-data flex items-center">
            <span className="peb-icon">
              <ProcedureIcon className="icon h-4 w-4 text-secondary" />
            </span>
            <p>
              {" "}
              <span className="text-secondary">
                {PM?.procedures?.title}
              </span>{" "}
              &nbsp; • &nbsp;
              <span className="text-primary">{procedureCount}</span>{" "}
            </p>
          </div>
        )}

        {inventoryPartsCount > 0 && (
          <div className="meta-data flex items-center">
            <span className="peb-icon">
              <PartIcon className="icon h-4 w-4 text-secondary" />
            </span>
            <p>
              {" "}
              <span className="text-secondary">{PM?.parts?.title}</span> &nbsp;
              • &nbsp;
              <span className="text-primary">{inventoryPartsCount}</span>{" "}
            </p>
          </div>
        )}
      </div>

      {canEdit || allowDeletion ? (
        <div className="flex items-center justify-end gap-md mt-md">
          {canEdit && (
            <Button
              variant={BUTTON_VARIANTS.OUTLINE}
              size={BUTTON_SIZES.SMALL}
              text={PM?.previewEvent.buttonEdit}
              leadingIcon={<PencilSimple size={16} />}
              onClick={onEditButtonClick}
            />
          )}
          {allowDeletion && (
            <Button
              variant={BUTTON_VARIANTS.DANGER}
              size={BUTTON_SIZES.SMALL}
              text={PM?.previewEvent.buttonDelete}
              leadingIcon={<Trash size={16} />}
              onClick={onDeleteButtonClick}
            />
          )}
        </div>
      ) : null}
    </section>
  );
};

export default PreviewEvent;
